import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useToast } from '../../components/Toast';
import { trackEvent } from '../../utils/eventTracker';
import { SupportGuideLayout, SupportHelpFooter, SupportLinkList, openChannelTalk } from './SupportGuide';
import { findSupportFaq, getFaqCategory, getRelatedFaqs } from './supportFaqIndex';

const stripHtml = (html) => html.replace(/<[^>]*>/g, '');

export const faqDescription = (faq) =>
  faq.seoDescription || `${stripHtml(faq.answer).replace(/\s+/g, ' ').trim().substring(0, 160)}...`;

const FaqDetailPage = () => {
  const { showToast } = useToast();
  const { id } = useParams();
  const [feedback, setFeedback] = useState(null);
  const faq = findSupportFaq(id);
  const category = getFaqCategory(id);
  const relatedFaqs = getRelatedFaqs(faq);
  const handleChat = () => openChannelTalk(showToast);

  useEffect(() => {
    setFeedback(null);
  }, [id]);

  const handleFeedback = (isHelpful) => {
    setFeedback(isHelpful);
    trackEvent('support_faq_feedback', { faq_id: id, helpful: isHelpful });
  };

  if (!faq) {
    return (
      <>
        <Helmet>
          <title>페이지를 찾을 수 없습니다 - 왓퀴즈 고객센터</title>
          <meta name="description" content="요청하신 FAQ 페이지를 찾을 수 없습니다. 다른 도움말을 찾아보시거나 고객센터로 문의해주세요." />
        </Helmet>

        <SupportGuideLayout current="찾을 수 없는 질문">
          <section className="sg__hero">
            <h1>이 질문을 찾을 수 없어요</h1>
            <p>주소가 바뀌었거나 없어진 질문이에요. 사용 가이드에서 다시 찾아보세요</p>
            <Link to="/support" className="sg__cta">사용 가이드로 가기</Link>
          </section>
          <SupportHelpFooter text="찾는 답이 없으면" onChat={handleChat} />
        </SupportGuideLayout>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{faq.question} - 왓퀴즈 고객센터</title>
        <meta name="description" content={faqDescription(faq)} />
        <meta name="keywords" content={faq.tags.join(', ')} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "QAPage",
            "mainEntity": {
              "@type": "Question",
              "name": faq.question,
              "text": faq.question,
              "answerCount": 1,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": stripHtml(faq.answer),
                "author": {
                  "@type": "Organization",
                  "name": "왓퀴즈"
                }
              }
            }
          })}
        </script>
        <meta property="og:title" content={`${faq.question} - 왓퀴즈 고객센터`} />
        <meta property="og:description" content={faqDescription(faq)} />
        <meta property="og:type" content="article" />
        <link rel="canonical" href={`https://whatquiz.co.kr/support/faq/${id}`} />
      </Helmet>

      <SupportGuideLayout current={category?.label || '자주 묻는 질문'} currentTo={category?.to}>
        <section className="sg__hero">
          <h1>{faq.question}</h1>
        </section>

        <article className="sg__card">
          <div
            className="sg__article sg__faq-body"
            dangerouslySetInnerHTML={{ __html: faq.answer }}
          />
        </article>

        <section className="sg__card" aria-label="답변 평가">
          {feedback === null ? (
            <div className="sg__feedback">
              <p>이 답변이 도움이 됐나요?</p>
              <button type="button" onClick={() => handleFeedback(true)}>도움이 됐어요</button>
              <button type="button" onClick={() => handleFeedback(false)}>아직 모르겠어요</button>
            </div>
          ) : (
            <p className="sg__feedback-result" role="status">
              {feedback ? '알려 주셔서 고마워요.' : '더 쉽게 고쳐 둘게요. 지금 바로 물어보셔도 돼요.'}
              {!feedback && <button type="button" onClick={handleChat}>채팅 상담</button>}
            </p>
          )}
        </section>

        {relatedFaqs.length > 0 && (
          <section className="sg__card">
            <h2>같이 많이 보는 질문</h2>
            <SupportLinkList
              items={relatedFaqs.map((item) => ({ to: `/support/faq/${item.id}`, title: item.question }))}
            />
          </section>
        )}

        <SupportHelpFooter onChat={handleChat} />
      </SupportGuideLayout>
    </>
  );
};

export default FaqDetailPage;
